import { useNavigate } from '@tanstack/react-router';
import { Check, ChevronsUpDown, LayoutGrid, Plus } from 'lucide-react';
import { useState } from 'react';

import { useWorkspacesList } from '@/shared/api/generated/workspaces/workspaces';
import { unwrap } from '@/shared/api/unwrap';
import { useSession } from '@/shared/auth';
import { cx } from '@/shared/lib/cx';
import { t } from '@/shared/i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { Skeleton } from '@/shared/ui/Skeleton';
import { Tooltip } from '@/shared/ui/Tooltip';
import { useCurrentWorkspace } from '@/shared/workspace/useCurrentWorkspace';

import { CreateWorkspaceDialog } from '@/pages/HomePage/CreateWorkspaceDialog';

import { Brand } from './Brand';

import styles from './WorkspaceSwitcher.module.scss';

/**
 * Текущее пространство и переход в другие.
 *
 * Внутри пространства заменяет собой знак Flow Do в шапке колонки: человек
 * работает в организации, а не в продукте, и её имя должно быть первым, что
 * он видит. Список остальных — в меню; владелец платформы может создать
 * новое отсюда же.
 */
export function WorkspaceSwitcher({ compact }: { compact: boolean }) {
  const { slug, workspace, isError } = useCurrentWorkspace();
  const session = useSession();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const list = useWorkspacesList({ query: { select: unwrap } });
  const isSuperAdmin = session.status === 'authenticated' && session.user.is_super_admin;

  if (isError) {
    return <Brand compact={compact} />;
  }

  if (!workspace) {
    return <Skeleton height={compact ? 28 : 36} width={compact ? 28 : '100%'} />;
  }

  const trigger = (
    <button
      type="button"
      className={cx(styles['switcher'], compact && styles['switcher--compact'])}
      aria-label={compact ? workspace.name : undefined}
    >
      <span className={styles['switcher__mark']} aria-hidden="true">
        {workspace.name.charAt(0).toUpperCase()}
      </span>
      {!compact && (
        <>
          <span className={styles['switcher__name']}>{workspace.name}</span>
          <ChevronsUpDown className={styles['switcher__chevron']} />
        </>
      )}
    </button>
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {compact ? (
            <Tooltip content={workspace.name} side="right">
              {trigger}
            </Tooltip>
          ) : (
            trigger
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={6}>
          {(list.data?.items ?? []).map((w) => (
            <DropdownMenuItem
              key={w.id}
              icon={w.slug === slug ? <Check /> : <span className={styles['dot']} />}
              onSelect={() => {
                void navigate({ to: '/app/w/$slug', params: { slug: w.slug } });
              }}
            >
              {w.name}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            icon={<LayoutGrid />}
            onSelect={() => {
              void navigate({ to: '/app' });
            }}
          >
            {t.nav.allWorkspaces}
          </DropdownMenuItem>
          {isSuperAdmin && (
            <DropdownMenuItem
              icon={<Plus />}
              onSelect={() => {
                setCreating(true);
              }}
            >
              {t.home.create}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateWorkspaceDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={() => {
          void list.refetch();
        }}
      />
    </>
  );
}
